import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

// Payment Form Component
const PaymentForm = ({ onSuccess, onError, amount, currency = 'USD' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error.message);
        onError?.(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm on backend
        try {
          await api.post(`/payments/confirm-intent/${paymentIntent.id}`);
          onSuccess?.(paymentIntent);
        } catch (backendError) {
          console.error('Backend confirmation error:', backendError);
          // Payment succeeded but backend update failed - still show success
          onSuccess?.(paymentIntent);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('Une erreur est survenue lors du paiement');
      onError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
        <PaymentElement 
          options={{
            layout: 'tabs',
            fields: {
              billingDetails: 'auto'
            }
          }}
        />
      </div>

      {errorMessage && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{errorMessage}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6"
        data-testid="pay-button"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Traitement en cours...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            Payer {amount} {currency}
          </>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
        <Lock className="w-3 h-3" />
        <span>Paiement sécurisé par Stripe</span>
      </div>
    </form>
  );
};

// Main Embedded Payment Component
export const EmbeddedPayment = ({ 
  amount, // Amount in major currency unit (e.g., 150 for $150)
  currency = 'USD',
  description,
  visaApplicationId,
  onSuccess,
  onError,
  metadata = {}
}) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Load Stripe and create Payment Intent
  useEffect(() => {
    const initPayment = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get Stripe publishable key
        const configRes = await api.get('/payments/stripe-config');
        const publishableKey = configRes.data.publishable_key;
        setStripePromise(loadStripe(publishableKey));

        // Create Payment Intent
        const amountInCents = Math.round(amount * 100);
        const intentRes = await api.post('/payments/create-intent', {
          amount: amountInCents,
          currency: currency.toLowerCase(),
          description,
          visa_application_id: visaApplicationId,
          metadata
        });

        setClientSecret(intentRes.data.client_secret);
      } catch (err) {
        console.error('Payment init error:', err);
        setError(err.response?.data?.detail || 'Erreur d\'initialisation du paiement');
      } finally {
        setLoading(false);
      }
    };

    if (amount > 0) {
      initPayment();
    }
  }, [amount, currency, description, visaApplicationId]);

  const handleSuccess = (paymentIntent) => {
    setPaymentSuccess(true);
    toast.success('Paiement effectué avec succès !');
    onSuccess?.(paymentIntent);
  };

  const handleError = (error) => {
    toast.error(error?.message || 'Erreur de paiement');
    onError?.(error);
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-sky-400" />
        <p className="mt-4 text-gray-400">Préparation du paiement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-400" />
        <p className="mt-4 text-red-400">{error}</p>
        <Button 
          onClick={() => window.location.reload()} 
          className="mt-4"
          variant="outline"
        >
          Réessayer
        </Button>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h3 className="mt-4 text-xl font-semibold text-white">Paiement Réussi !</h3>
        <p className="mt-2 text-gray-400">
          Votre paiement de {amount} {currency} a été accepté.
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Un email de confirmation vous a été envoyé.
        </p>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Payment Summary */}
      <div className="p-4 rounded-lg bg-gradient-to-r from-sky-500/10 to-purple-500/10 border border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-sky-400" />
            <div>
              <p className="text-sm text-gray-400">Montant à payer</p>
              <p className="text-2xl font-bold text-white">{amount} {currency}</p>
            </div>
          </div>
        </div>
        {description && (
          <p className="mt-2 text-sm text-gray-400">{description}</p>
        )}
      </div>

      {/* Stripe Elements */}
      <Elements 
        stripe={stripePromise} 
        options={{
          clientSecret,
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#0ea5e9',
              colorBackground: '#0f172a',
              colorText: '#ffffff',
              colorDanger: '#ef4444',
              fontFamily: 'Inter, system-ui, sans-serif',
              borderRadius: '8px',
            },
            rules: {
              '.Input': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
              '.Input:focus': {
                border: '1px solid #0ea5e9',
                boxShadow: '0 0 0 1px #0ea5e9',
              },
              '.Label': {
                color: '#9ca3af',
              }
            }
          }
        }}
      >
        <PaymentForm 
          onSuccess={handleSuccess}
          onError={handleError}
          amount={amount}
          currency={currency}
        />
      </Elements>
    </div>
  );
};

export default EmbeddedPayment;
